import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Target, Check, X, AlertTriangle, Crown } from "lucide-react";

const API = '/api';

export default function ShootOffs() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [shootoffs, setShootoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false);
  const [currentShootoff, setCurrentShootoff] = useState(null);
  const [shootoffScores, setShootoffScores] = useState({});

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchLeaderboard();
      fetchShootoffs();
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?t=${Date.now()}`);
      setEvents(res.data);
      if (res.data.length > 0) {
        setSelectedEvent(res.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/${selectedEvent}?t=${Date.now()}`);
      setLeaderboardData(res.data);
    } catch (error) {
      toast.error("Failed to fetch leaderboard");
    }
  };

  const fetchShootoffs = async () => {
    try {
      const res = await axios.get(`${API}/shootoffs?event_id=${selectedEvent}&t=${Date.now()}`);
      setShootoffs(res.data);
    } catch (error) {
      toast.error("Failed to fetch shoot-offs");
    }
  };

  const createShootoff = async (position, tiedShooters, tiedScore) => {
    try {
      await axios.post(`${API}/shootoffs`, {
        event_id: selectedEvent,
        position: position,
        tied_shooter_ids: tiedShooters.map(s => s.shooter_id),
        tied_shooter_names: tiedShooters.map(s => s.shooter_name),
        tied_score: tiedScore
      });
      toast.success(`Shoot-off created for ${position} place`);
      setTimeout(() => fetchShootoffs(), 100);
    } catch (error) {
      toast.error("Failed to create shoot-off");
    }
  };

  const openScoringDialog = (shootoff) => {
    setCurrentShootoff(shootoff);
    const initialScores = {};
    shootoff.tied_shooter_ids.forEach(id => {
      initialScores[id] = { hits: 0, streak: 0 };
    });
    setShootoffScores(initialScores);
    setScoringDialogOpen(true);
  };

  const updateShootoffScore = (shooterId, field, value) => {
    setShootoffScores(prev => ({
      ...prev,
      [shooterId]: {
        ...prev[shooterId],
        [field]: parseInt(value) || 0
      }
    }));
  };

  const submitShootoffResult = async () => {
    if (!currentShootoff) return;

    // Determine winner: highest hits, then highest streak as tiebreaker
    let winnerId = null;
    let winnerName = null;
    let highestHits = -1;
    let highestStreak = -1;

    currentShootoff.tied_shooter_ids.forEach((id, idx) => {
      const score = shootoffScores[id];
      if (score.hits > highestHits || 
          (score.hits === highestHits && score.streak > highestStreak)) {
        highestHits = score.hits;
        highestStreak = score.streak;
        winnerId = id;
        winnerName = currentShootoff.tied_shooter_names[idx];
      }
    });

    if (!winnerId) {
      toast.error("Could not determine winner - scores may still be tied");
      return;
    }

    try {
      await axios.put(`${API}/shootoffs/${currentShootoff.id}/result`, {
        winner_id: winnerId,
        winner_name: winnerName
      });
      toast.success(`${winnerName} wins the shoot-off!`);
      setScoringDialogOpen(false);
      setTimeout(() => fetchShootoffs(), 100);
      fetchLeaderboard();
    } catch (error) {
      toast.error("Failed to submit result");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  const hasTies = leaderboardData?.ties && Object.values(leaderboardData.ties).some(t => t.length > 1);

  return (
    <div data-testid="shootoffs-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Shoot-Offs
          </h1>
          <p className="text-slate-500 mt-1">Manage tie-breaker shoot-offs for top positions</p>
        </div>

        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="w-64" data-testid="shootoff-event-select">
            <SelectValue placeholder="Select event" />
          </SelectTrigger>
          <SelectContent>
            {events.map((event) => (
              <SelectItem key={event.id} value={event.id}>
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Detected Ties */}
      {hasTies && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl flex items-center gap-2 text-orange-700">
              <AlertTriangle />
              Ties Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(leaderboardData.ties).map(([position, tiedShooters]) => {
                if (tiedShooters.length <= 1) return null;
                const existingShootoff = shootoffs.find(s => s.position === position);
                
                return (
                  <div key={position} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="font-bold text-lg">{position} Place Tie</span>
                      <p className="text-sm text-slate-600">
                        {tiedShooters.map(s => s.shooter_name).join(" vs ")} 
                        <span className="ml-2 font-mono">({tiedShooters[0].best_score} pts)</span>
                      </p>
                    </div>
                    {existingShootoff ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm">
                        Shoot-off Created
                      </span>
                    ) : (
                      <Button
                        onClick={() => createShootoff(position, tiedShooters, tiedShooters[0].best_score)}
                        className="bg-orange-500 hover:bg-orange-600"
                        data-testid={`create-shootoff-${position}`}
                      >
                        Create Shoot-Off
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Shoot-offs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Target className="text-[#1B3B6F]" />
            Shoot-Offs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {shootoffs.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              No shoot-offs created yet. Ties will appear above when detected.
            </p>
          ) : (
            <div className="space-y-4">
              {shootoffs.map((shootoff) => (
                <div
                  key={shootoff.id}
                  className={`p-4 rounded-lg border-2 ${
                    shootoff.status === "completed" 
                      ? "border-green-200 bg-green-50" 
                      : "border-orange-200 bg-orange-50"
                  }`}
                  data-testid={`shootoff-${shootoff.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Crown className={shootoff.status === "completed" ? "text-green-600" : "text-orange-500"} size={20} />
                        <span className="font-bold text-lg">{shootoff.position} Place Shoot-Off</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          shootoff.status === "completed" 
                            ? "bg-green-200 text-green-800" 
                            : "bg-orange-200 text-orange-800"
                        }`}>
                          {shootoff.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Participants: {shootoff.tied_shooter_names.join(" vs ")}
                      </p>
                      {shootoff.winner_name && (
                        <p className="text-sm text-green-700 font-medium mt-1">
                          Winner: {shootoff.winner_name}
                        </p>
                      )}
                    </div>
                    
                    {shootoff.status === "pending" && (
                      <Button
                        onClick={() => openScoringDialog(shootoff)}
                        className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                        data-testid={`score-shootoff-${shootoff.id}`}
                      >
                        <Target size={18} className="mr-2" />
                        Score Shoot-Off
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scoring Dialog */}
      <Dialog open={scoringDialogOpen} onOpenChange={setScoringDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Score Shoot-Off: {currentShootoff?.position} Place
            </DialogTitle>
            <DialogDescription>
              Enter the shoot-off scores for each participant. Winner determined by hits, then streak.
            </DialogDescription>
          </DialogHeader>
          
          {currentShootoff && (
            <div className="space-y-4 mt-4">
              {currentShootoff.tied_shooter_ids.map((shooterId, idx) => (
                <div key={shooterId} className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium mb-3">{currentShootoff.tied_shooter_names[idx]}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-600">Hits</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full mt-1 p-2 border rounded font-mono text-lg"
                        value={shootoffScores[shooterId]?.hits || 0}
                        onChange={(e) => updateShootoffScore(shooterId, "hits", e.target.value)}
                        data-testid={`shootoff-hits-${shooterId}`}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Longest Streak</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full mt-1 p-2 border rounded font-mono text-lg"
                        value={shootoffScores[shooterId]?.streak || 0}
                        onChange={(e) => updateShootoffScore(shooterId, "streak", e.target.value)}
                        data-testid={`shootoff-streak-${shooterId}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                onClick={submitShootoffResult}
                className="w-full bg-green-700 hover:bg-green-600"
                data-testid="submit-shootoff-result"
              >
                <Trophy size={18} className="mr-2" />
                Submit Result & Declare Winner
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
